import { Fragment, h } from "preact";
import { useState } from "preact/hooks";
import { useSelector } from "react-redux";

import i18n from "../../i18n.js";
import { selectCurrentUser } from "../../store/index";
import StepCircle from "../referralPage/StepCircle.jsx";
import FAQ from "../referralPage/FAQ.jsx";
import TextField from "../TextField.jsx";
import Button from "../Button.jsx";
import List from "../List.jsx";
import ListItem from "../ListItem.jsx";
import ReferralShareDialog from "../referralPage/ReferralShareDialog.jsx";

export default function CompanyReferralSection({ referralLink }) {
  const user = useSelector(selectCurrentUser);
  const [showCopyNote, setShowCopyNote] = useState(false);
  const [shareOptionsDialogOpen, setShareOptionsDialogOpen] = useState(false);

  function handleCopyClick() {
    navigator.clipboard.writeText(referralLink.link);
    clearTimeout(showCopyNote);
    setShowCopyNote(
      setTimeout(() => {
        setShowCopyNote(false);
      }, 1500)
    );
  }

  function handleShareClick() {
    setShareOptionsDialogOpen(true);
  }

  const faqItems = [
    {
      question: "Who can I refer?",
      answer:
        "You can refer any friend or colleague in search of international IT candidates. Please note that anyone who previously received a referral invitation is not eligible.",
    },
    {
      question: "Where can I read the full terms?",
      answer:
        'You can read the program\'s terms and conditions <a href="https://nioomi.com/terms/partner">here</a>. The rules and limitations in this FAQ also apply to the program.',
    },
    {
      question: "How do I send a referral?",
      answer: `
      <strong>Please note:</strong> You must have received acceptance to the Nioomi talent pool to access the referral program. 
      <ol>
        <li>Sign in to <a href="https://nioomi.com/login">nioomi.com</a>.</li>
        <li>Click your avatar (profile picture or initials) in the top-right corner.</li>
        <li>Click "Business referral program".</li>
        <li>Find your personal link, and share it wherever you’d like.</li>
      </ol>
      `,
    },
    {
      question: "How does my contact accept a referral?",
      answer: `
      <ol>
        <li>Click the referral link in the email or the link shared with them, and follow the prompts to create a Nioomi business account.</li>
	      <li>Verify their account's email address.</li>
	      <li>Receive a mail within the next 48h if their company meets the criteria to enter the Nioomi talent pool.</li>
      </ol>
      `,
    },
    {
      question: "How do I know if my contact received their invitation?",
      answer:
        "You can track when your referral registers with Nioomi, create their company profile, and be informed when the first Nioomi talent has been hired. Every step is transparently shown to you within the app.",
    },
    {
      question:
        "Will I still get credit if my contact signs up with a different email address?",
      answer:
        "You'll earn your cash reward as long as your contact signs up for an account with the referral link in your invitation. Your contact doesn't have to use the email address you used to send the invitation.	However, if your friend receives referrals from multiple senders, only the sender of the used referral link will earn the cash reward.",
    },
    {
      question: "When do I get my cash reward?",
      answer:
        "We'll send your reward directly to your account about 2-3 weeks after the referred client hires a Nioomi talent. Depending on your payout method, the funds can take another 1-7 days to arrive in your account.<br/>If your referral is successful, we will require your account information to send you the cash reward. We will contact you once this happens to obtain your account information.",
    },
    {
      question: "Does the referral expire?",
      answer:
        "Yes. To qualify for your cash reward, the person you refer must sign up and create a business profile within 90 days of acting on the referral by clicking your unique link.",
    },
    {
      question: "How many referrals can I make?",
      answer:
        "You can earn a cash reward for a maximum of 25 referrals. But there's no limit to how many referrals you can send out.",
    },
  ];

  if (!referralLink) return null;

  return (
    <Fragment>
      <div class="referral-page referral-page--partner-referral mt-48">
        <div class="row">
          <div class="referral-page__cell col s12 l6 bg-lavender pl-32 pr-32 pb-64">
            <h1 class="t-h4 t-h4--margin mt-48">
              {i18n.t("pages.partner_referral.title")}
            </h1>
            <p class="t-body3 mb-32">
              {i18n.t("pages.partner_referral.intro")}
            </p>
            <a
              class="t-body3 underline font-medium"
              target="_blank"
              href="/terms/partner"
            >
              {i18n.t("pages.partner_referral.terms")}
            </a>

            <div class="mt-48 mb-48">
              <TextField
                data-testid="referral-link-input"
                value={referralLink.link}
              />
            </div>
            <Button noHighlight onClick={handleShareClick}>
              {i18n.t("pages.partner_referral.share")}
            </Button>
            <Button noHighlight text big onClick={handleCopyClick}>
              {showCopyNote
                ? i18n.t("pages.partner_referral.copy_link_copied")
                : i18n.t("pages.partner_referral.copy_link")}
            </Button>
          </div>
          <div class="referral-page__cell col s12 l6 referral-page__hero"></div>
        </div>

        <section class="mt-96 mb-96">
          <h2 class="t-h5 t-h5--margin mt-48">
            {i18n.t("pages.partner_referral.step_section.title")}
          </h2>
          <div class="row row--unpadded">
            <div class="col s12 m4 l3 pt-12">
              <StepCircle lavender number={1} />
              <h3 class="t-body2 font-bold mt-32 pb-8">
                {i18n.t("pages.partner_referral.step_section.step1.title")}
              </h3>
              <p class="t-body3 mb-64 color-shades-charcoal">
                {i18n.t("pages.partner_referral.step_section.step1.desc")}
              </p>
            </div>
            <div class="col s12 m4 l3 pt-12">
              <StepCircle lavender number={2} />
              <h3 class="t-body2 font-bold mt-32 pb-8">
                {i18n.t("pages.partner_referral.step_section.step2.title")}
              </h3>
              <p class="t-body3 mb-64 color-shades-charcoal">
                {i18n.t("pages.partner_referral.step_section.step2.desc")}
              </p>
            </div>
            <div class="col s12 m4 l3 pt-12">
              <StepCircle lavender number={3} />
              <h3 class="t-body2 font-bold mt-32 pb-8">
                {i18n.t("pages.partner_referral.step_section.step3.title")}
              </h3>
              <p class="t-body3 color-shades-charcoal">
                {i18n.t("pages.partner_referral.step_section.step3.desc")}
              </p>
            </div>
          </div>
        </section>
        <section>
          <div class="nio-side-by-side nio-side-by-side--large-gap nio-side-by-side--image-right">
            <div class="text-right">
              <img
                class="illustation"
                src="/images/pages/partner/standing_guy.jpg"
                alt="Portrait of a male Egyptian recruiter in front of office"
                style="max-height: 512px"
              />
            </div>

            <div class="nio-side-by-side__content">
              <h2 class="t-h5 t-h5--margin">
                {i18n.t("pages.partner_referral.benefit_section.title")}
              </h2>
              <p class="t-body2 color-shades-charcoal pb-0 pt-0">
                {i18n.t("pages.partner_referral.benefit_section.description")}
              </p>

              <List class="mt-48">
                <ListItem iconFront="check">
                  {i18n.t("pages.partner_referral.benefit_section.benefit1")}
                </ListItem>
                <ListItem iconFront="check">
                  {i18n.t("pages.partner_referral.benefit_section.benefit2")}
                </ListItem>
                <ListItem iconFront="check">
                  {i18n.t("pages.partner_referral.benefit_section.benefit3")}
                </ListItem>
              </List>
            </div>
          </div>
        </section>
        <section class="mt-64">
          <FAQ items={faqItems} />
        </section>
      </div>

      {shareOptionsDialogOpen && (
        <ReferralShareDialog
          i18nPrefix="component.share_referral_dialog.b2b"
          onClose={() => setShareOptionsDialogOpen(false)}
          referralLink={referralLink}
          user={user}
        />
      )}
    </Fragment>
  );
}
